/**
 * Created by Carsten Bühler
 * User: CBuehler on 30.09.2015
 * Copyright: xdot GmbH 2015
 */

import { Dispatcher } from 'flux';
import AsyncState from '../constants/AsyncState';
import { handleDataUpdate } from '../utils/DataUpdateHandler';
import RemoteDataActions, { activeStores } from '../actions/RemoteDataActions';
import deepEqual from '../utils/deepEqual';

let isInRemoteDataHandling = false;

let n = 0;
const asyncKey = () => 'ASY:' + n++;

let waitingQueue = [];

function checkAsyncDone(key) {
    if (__DEBUG__) {
        activeStores().forEach(store => {
            store.getRemoteState && Object.keys(store.getRemoteState()).map(name => store.getRemoteState()[ name ]).forEach(d => {
                if (d._asyncKey === key && d._loading) {
                    console.error('Async: ' + key + ' done but data is', d);
                    debugger;
                }
            });
        });
    }
}

/**
 *
 * Custom Dispatcher extended for the Liferay-Widget-React-Framework (Lwr-Framework).
 * Implements Actions of type: https://github.com/acdlite/flux-standard-action
 * Implements dispatches and asnyc dispatches for actions including callbacks, etc.
 *
 */
export default class LwrDispatcher extends Dispatcher {

    /*
     * @ref https://github.com/acdlite/flux-standard-action
     *
     * ## Actions
     *
     * An action MUST
     * - be a plain JavaScript object.
     * - have a `type` property.
     *
     * An action MAY
     * - have a `error` property.
     * - have a `payload` property.
     * - have a `meta` property.
     *
     * An action MUST NOT include properties other than `type`, `payload`, and `error`, and `meta`.
     *
     * ### `type`
     *
     * The `type` of an action identifies to the consumer the nature of the action that has occurred.
     * Two actions with the same `type` MUST be strictly equivalent (using `===`).
     * By convention, `type` is usually string constant or a Symbol.
     *
     * ### `payload`
     *
     * The optional `payload` property MAY be any type of value.
     * It represents the payload of the action.
     * Any information about the action that is not the `type` or status of the action should be part of the `payload` field.
     *
     * By convention, if `error` is `true`, the `payload` SHOULD be an error object.
     * This is akin to rejecting a promise with an error object.
     *
     * ### `error`
     *
     * The optional `error` property MAY be set to `true` if the action represents an error.
     *
     * An action whose `error` is true is analogous to a rejected Promise.
     * By convention, the `payload` SHOULD be an error object.
     *
     * If `error` has any other value besides `true`,
     * including `undefined` and `null`, the action MUST NOT be interpreted as an error.
     *
     * ### `meta`
     *
     * The optional `meta` property MAY be any type of value.
     * It is intended for any extra information that is not part of the payload.
     *
     */


    constructor() {
        super();
        this._afterDispatchListener = null;
    }

    /**
     * @param type
     * @param fn
     * @param data
     * @param {Object} options
     * @param options.meta the action meta
     * @param options.payload the action.payload
     * @param options.beforeSuccess
     * @param options.afterSuccess
     * @param options.beforeError
     * @param options.afterError
     * @returns {*}
     */
    dispatchAsyncOperation(type, fn, data, options) {
        if (this.isDispatching()) {
            console.warn("Dispatch Async until dispatch ");
            waitingQueue.push({ asyncDispatch: true, args: arguments });
            return;
        }

        const key = asyncKey();

        const toAction = (asyncState, payload = {}) => ({
            type,
            payload: {
                ...payload,
                ...options.payload
            },
            meta: {
                asyncState,
                key: key,
                ...options.meta
            }
        });

        const handler = (asyncState, callbackName, result, meta) => {
            if (this.isDispatching()) {
                console.error('Error in async-handler with arguments', arguments);
            }

            let action = toAction(asyncState, result);
            if (asyncState === AsyncState.ERROR) {
                console.warn(result);
                action = asError(action, result);
            }
            action = (options[ 'before' + callbackName ] && options[ 'before' + callbackName ](action)) || action;
            this.dispatch(action);
            options[ 'after' + callbackName ] && options[ 'after' + callbackName ](action);
            handleDataUpdate(meta);
        };

        if (fn) {
            this.dispatch(toAction(AsyncState.REQUESTED, data));
            let apiCall = fn(data);
            (apiCall.promise || apiCall).then((result) => {
                handler(AsyncState.SUCCESS, 'Success', result, apiCall.meta);
                checkAsyncDone(key);
            }).catch((result) => {
                console.error(result);
                handler(AsyncState.ERROR, 'Error', result, apiCall.meta);
                checkAsyncDone(key);
            });

        } else {
            console.error('API METHOD expected!');
            handler(AsyncState.ERROR, 'Error');
            checkAsyncDone(key);
        }


        return key;
    }

    dispatch(payload) {
        if (this.isDispatching()) {
            let idx = waitingQueue.findIndex(p => deepEqual(p, payload));
            if (idx < 0) {
                console.warn("Dispatch until dispatch ");
                waitingQueue.push(payload);
            } else {
                console.warn("Dispatch until dispatch (existing payload)");
            }
            return;
        }
        if (!isFSA(payload)) {
            console.warn('DISPATCH NO FSA', payload);
        }
        try {
            if (LOGS.DISPATCHER) {
                if (isDataAction(payload)) {
                    console.group('DISPATCH', payload, payload && payload.meta && payload.meta.name);
                } else {
                    console.group('DISPATCH', payload);
                }
            }
            super.dispatch(payload);
            if (this._afterDispatchListener) {
                this._afterDispatchListener();
            }
            this.handleRemoteData();
        } finally {
            console.groupEnd();
        }
        this.handleQueue();
    }

    handleQueue() {
        if (waitingQueue.length > 0) {
            if (waitingQueue.length > 1) {
                console.error('Large queue ', waitingQueue);
            }
            const waiting = waitingQueue;
            waitingQueue = [];
            setTimeout(() => {
                for (let i = waiting.length - 1; i >= 0; i--) {
                    const p = waiting[ i ];
                    if (p.asyncDispatch) {
                        console.warn("Dispatch queued async payload ", p.args);
                        this.dispatchAsyncOperation.apply(this, p.args);

                    } else {
                        console.warn("Dispatch queued payload ", p);
                        this.dispatch(p);
                    }
                }
            });
        }
    }

    handleRemoteData() {
        if (!isInRemoteDataHandling) {
            try {
                isInRemoteDataHandling = true;
                RemoteDataActions.refreshRemoteData();
            } catch (e) {
                console.error(e);
            } finally {
                isInRemoteDataHandling = false;
            }
        }
    }

    isDispatching() {
        if (this._isDispatching && typeof this._isDispatching === 'function') {
            return this._isDispatching();
        }
        return this._isDispatching;
    }

    registerAfterDispatchListener(listener) {
        this._afterDispatchListener = listener;
    }
}


const validKeys = [
    'type',
    'payload',
    'error',
    'meta'
];

function isValidKey(key) {
    return validKeys.indexOf(key) > -1;
}

function isFSA(action) {
    return (
        (isPlainObject(action) &&
            typeof action.type !== 'undefined' &&
            Object.keys(action).every(isValidKey))
    );
}

function isDataAction(action) {
    return action.type.indexOf('DATA_') === 0;
}

export function isError(action) {
    return action.error === true;
}

function asError(action, result) {
    action.error = true;
    if ((result && result.message) || action.payload.error) {
        action.payload.error = { message: result.message || result.error.message };
    }
    return action;
}

//from lodash
function isPlainObject(value) {
    if (!(!!value && typeof value === 'object')) {
        return false;
    }
    let proto = Object.prototype;
    if (typeof value.constructor === 'function') {
        proto = Object.getPrototypeOf(value);
    }
    if (proto === null) {
        return true;
    }
    let Ctor = proto.constructor;
    return (typeof Ctor === 'function' &&
        Ctor instanceof Ctor && Function.prototype.toString.call(Ctor) === Function.prototype.toString.call(Object));
}





// WEBPACK FOOTER //
// ./js/dispatcher/LwrDispatcher.js